import React, { ReactNode, useCallback, useContext, useState } from 'react'
import styled from 'styled-components'
import cn from 'clsx'
import { rem } from 'polished'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import LuxuryEscapesSmallLogo from 'components/App/Header/Logo/logos/LuxuryEscapesSmallLogo'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import { isSenderLE, isSenderSystem, isSenderUser } from 'supportAssistant/utils/messageUtils'
import FeedbackModal from 'supportAssistant/components/Chat/FeedbackModal'
import Loading from 'components/Common/Loading/LoadingAnimation'
import LineThumbsDownIcon from 'components/Luxkit/Icons/line/LineThumbsDownIcon'
import LineThumbsUpIcon from 'components/Luxkit/Icons/line/LineThumbsUpIcon'
import SolidThumbsDownIcon from 'components/Luxkit/Icons/solid/SolidThumbsDownIcon'
import SolidThumbsUpIcon from 'components/Luxkit/Icons/solid/SolidThumbsUpIcon'
import ModalContext from 'contexts/ModalContext'
import AgentAvatar from 'supportAssistant/components/Support/AgentAvatar'
import { MessageSenders } from 'supportAssistant/constants/messageSenders'

const Container = styled.div`
  display: grid;
  grid-gap: ${rem(2)};
  grid-template-columns: min-content 1fr;
  grid-template-areas:
    "avatar message"
    ". footer"
    ". feedback";
  width: fit-content;
  max-width: ${rem(280)};

  ${mediaQueryUp.desktop} {
    max-width: ${rem(380)};
  }

  ${mediaQueryUp.largeDesktop} {
    max-width: calc(50% + ${rem(60)});
  }

  &.is-le-sender {
    ${mediaQueryUp.desktop} {
      grid-template-columns: min-content 1fr ${rem(60)};
      grid-template-areas:
        "avatar message feedback"
        ". footer .";
    }
  }

  &.is-system-sender {
    grid-template-columns: auto;
    grid-template-areas: "message";
    margin: auto;
  }

  &.is-user-sender {
    grid-template-columns: 1fr min-content;
    grid-template-areas:
      "message avatar"
      "footer .";
    justify-self: end;
    margin-left: auto;
  }


  &.is-user-sender + :is(.is-le-sender, .is-system-sender),
  &.is-le-sender + :is(.is-user-sender, .is-system-sender),
  &.is-system-sender + :is(.is-user-sender, .is-le-sender) {
    margin-top: ${rem(16)};
  }

  .compact ol, ul {
    counter-reset: item;

    > li {
      margin-top: ${rem(8)};

      &::before {
        content: counter(item) ": ";
        counter-increment: item;
      }

      p {
        display: contents;
      }
    }
  }

  // hover on a message should control visibility of thumb-icons
  &:hover {
    .feedback {
      opacity: 1;
      visibility: visible;
      pointer-events: auto;
      transition: opacity 0.2s 0.3s;
    }

    .thumb-up:not(.fade-out),
    .thumb-down:not(.fade-out) {
      cursor: pointer;
      visibility: visible;
      opacity: 1;
      pointer-events: auto;
      transition: opacity 0.2s 0.3s;
    }
  }
`

const Avatar = styled.div`
  grid-area: avatar;
  width: ${rem(24)};
  height: ${rem(24)};
  align-self: end;
`

const MessageContent = styled.div`
  grid-area: message;
  padding: ${rem(8)};
  border-radius: ${rem(4)};
  background-color: ${({ theme }) => theme.palette.neutral.seven};
  color: ${({ theme }) => theme.palette.neutral.one};

  &:not(.is-user-sender) {
    margin-right: auto;
    margin-left: ${rem(8)};
  }

  &.is-user-sender {
    background-color: ${({ theme }) => theme.palette.neutral.one};
    color: ${({ theme }) => theme.palette.neutral.seven};

  }
`

const SystemMessageContent = styled.div`
  grid-area: message;
  text-align: center;
`

const FooterContent = styled.div`
  grid-area: footer;
  margin-top: ${rem(4)};
`

const MessageFeedback = styled.div`
  grid-area: feedback;
  min-height: ${rem(24)};
  display: grid;
  position: relative;
  grid-template-columns: auto auto;
  justify-content: end;

  // hide thumb-icons only on desktop for hover funtionality
  ${mediaQueryUp.desktop} {
    align-items: end;
    justify-content: start;
    margin-left: ${rem(4)};
    margin-bottom: ${rem(4)};
    transition: opacity 0.2s 0.3s, visibility 0s 0.5s;

    &:not(.has-feedback) {
      opacity: 0;
      visibility: hidden;
    }
  }

  .thumb-up,
  .thumb-down {
    position: absolute;

    &.always-visible {
      opacity: 1;
      visibility: visible;

      ${mediaQueryDown.tablet} {
        right: ${rem(2)};
      }

      ${mediaQueryUp.desktop} {
        left: ${rem(2)};
      }
    }

    // hide selected thumb-icon immediately
    &.hidden {
      display: none;
    }

    // fade-out not selected thumb-icon
    &.fade-out {
      -webkit-animation: fadeOut .25s ease-out;
      animation: fadeOut .25s ease-out;
      opacity: 0;
      margin: 0;

      @-webkit-keyframes fadeOut {
        0% {
          opacity: 1;
        }
        100% {
          opacity: 0;
        }
      }
    
      @keyframes fadeOut {
        0% {
          opacity: 1;
        }
        100% {
          opacity: 0;
        }
      }
    }

    &:not(.thumb-down) {
      ${mediaQueryDown.tablet} {
        right: ${rem(32)};
        bottom: 0;

        &.always-visible {
          right: ${rem(2)};
        }

        &.move-feedback {
          -webkit-animation: move .25s ease-out;
          animation: move .25s ease-out;

          @-webkit-keyframes move {
              0% {right: ${rem(32)};}
            100% {right: ${rem(2)};}
          }
          
          @keyframes move {
              0% {right: ${rem(32)};}
            100% {right: ${rem(2)};}
          }
        }
      }

      ${mediaQueryUp.desktop} {
        left: ${rem(2)};
        bottom: 0;

        &.always-visible {
          left: ${rem(2)};
        }
      }
    }

    &:not(.thumb-up) {
      ${mediaQueryDown.tablet} {
        right: ${rem(2)};
        bottom: 0;

        &.always-visible {
          right: ${rem(2)};
        }
      }

      ${mediaQueryUp.desktop} {
        left: ${rem(32)};
        bottom: 0;
  
        &.always-visible {
          left: ${rem(2)};
        }

        &.move-feedback {
          -webkit-animation: move .25s ease-out;
          animation: move .25s ease-out;
  
          @-webkit-keyframes move {
              0% {left: ${rem(32)};}
            100% {left: ${rem(2)};}
          }
      
          @keyframes move {
              0% {left: ${rem(32)};}
            100% {left: ${rem(2)};}
          }
        }
      }
  }
`

const StyledBodyText = styled(BodyText)`
  display: inline;
  word-break: break-word;
  white-space: pre-wrap;
  text-align: initial;
`

interface MessageProps {
  message: any;
  footer?: ReactNode;
  onMessageFeedbackSubmit?: (feedback: { id: string, type: string }) => void;
}

const Message: React.FC<MessageProps> = ({
  message,
  footer,
  onMessageFeedbackSubmit,
}) => {
  const [feedback, setFeedback] = useState(message.feedback ?? null)
  const [playFeedbackAnimation, setPlayFeedbackAnimation] = useState(false)

  const showModal = useContext(ModalContext)

  const onFeedbackClick = useCallback(async(feedback) => {
    setPlayFeedbackAnimation(true)
    setFeedback(feedback.sentiment)
    const userFeedback = await showModal<undefined | any>(<FeedbackModal sentiment={feedback.sentiment} />)

    if (!(userFeedback?.issues?.length || userFeedback?.message)) {
      setFeedback(null)
      return
    }

    onMessageFeedbackSubmit?.({
      ...feedback,
      ...userFeedback,
    })
  }, [onMessageFeedbackSubmit, showModal])

  return (
    <Container className={cn({ 'is-user-sender': isSenderUser(message.sender), 'is-le-sender': isSenderLE(message.sender), 'is-system-sender': isSenderSystem(message.sender) })}>
      {isSenderLE(message.sender) && <Avatar>
        {message.sender === MessageSenders.CHATBOT && <LuxuryEscapesSmallLogo height={24} width={24} />}
        {message.sender === MessageSenders.AGENT && <AgentAvatar width={24} height={24} />}
      </Avatar>}
      {!isSenderSystem(message.sender) && <MessageContent className={cn({ 'is-user-sender': isSenderUser(message.sender), 'is-le-sender': !isSenderUser(message.sender) })}>
        {!message.isTyping && isSenderLE(message.sender) && <MarkdownRender content={message.text} type="compact" fontSize="medium" />}
        {!message.isTyping && isSenderUser(message.sender) && <StyledBodyText variant="medium">{message.text}</StyledBodyText>}
        {message.isTyping && <Loading size={6} />}
      </MessageContent>}
      {isSenderSystem(message.sender) && <SystemMessageContent className={cn({ 'is-system-sender': isSenderSystem(message.sender) })}>
        <StyledBodyText variant="small" colour="neutral-two">{message.text}</StyledBodyText>
      </SystemMessageContent>}
      {footer && (
        <FooterContent>
          {footer}
        </FooterContent>
      )}
      {isSenderLE(message.sender) && onMessageFeedbackSubmit && <MessageFeedback className={cn('feedback', { 'has-feedback': feedback })}>
        {feedback && feedback === 'POSITIVE' && <SolidThumbsUpIcon className={cn({ 'move-feedback': playFeedbackAnimation }, 'thumb-up', 'always-visible')} colour="neutral-four" size="S" />}
        <LineThumbsUpIcon onClick={() => onFeedbackClick({ messageId: message.id, sentiment: 'POSITIVE' })} className={cn('thumb-up', { 'fade-out': feedback, hidden: feedback === 'POSITIVE' })} colour="neutral-four" size="S" />
        <LineThumbsDownIcon onClick={() => onFeedbackClick({ messageId: message.id, sentiment: 'NEGATIVE' })} className={cn('thumb-down', { 'fade-out': feedback, hidden: feedback === 'NEGATIVE' })} colour="neutral-four" size="S" />
        {feedback && feedback === 'NEGATIVE' && <SolidThumbsDownIcon className={cn({ 'move-feedback': playFeedbackAnimation }, 'thumb-down', 'always-visible')} colour="neutral-four" size="S" />}
      </MessageFeedback>}
    </Container>
  )
}

export default Message
